import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip, Typography } from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { SegmentService } from '../../../services/segment.service';
import { Segment } from '../../../interfaces/accounting.interface';
import { FpaService, KpiResult, KpiSettings } from '../../../services/fpa.service';
import SearchableSelect from '../../common/SearchableSelect';
import { toLocalIsoDate } from '../../../utils/dateUtils';

export const formatKpi = (value: number | null, unit: string): string => {
    if (value === null || value === undefined) return 'n/a';
    const t = value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return unit === '%' ? `${t}%` : unit === 'days' ? `${t} days` : unit === 'x' ? `${t}x` : t;
};

const PREFIX_FIELDS: [keyof KpiSettings, string][] = [
    ['currentAssetPrefixes', 'Current assets'], ['currentLiabilityPrefixes', 'Current liabilities'], ['inventoryPrefixes', 'Inventory'],
    ['receivablePrefixes', 'Receivables'], ['payablePrefixes', 'Payables'], ['revenuePrefixes', 'Revenue'], ['cogsPrefixes', 'Cost of revenue'],
    ['interestPrefixes', 'Interest'], ['taxPrefixes', 'Tax'], ['debtPrefixes', 'Debt'],
];

const KpiPack: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.BUDGET_MANAGE);
    const [asOf, setAsOf] = useState(toLocalIsoDate());
    const [segmentId, setSegmentId] = useState('');
    const [segments, setSegments] = useState<Segment[]>([]);
    const [result, setResult] = useState<KpiResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState<KpiSettings | null>(null);
    const [editOpen, setEditOpen] = useState(false);

    const load = useCallback(async () => {
        try { setLoading(true); setResult(await FpaService.getKpis(asOf, segmentId || undefined)); }
        catch (err) { toast.showApiError(err, 'Failed to compute the KPIs'); }
        finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asOf, segmentId]);

    useEffect(() => {
        load();
        SegmentService.getByInstitution(0, 200).then(p => setSegments(p.content ?? p)).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openSettings = async () => {
        try { setSettings(await FpaService.getKpiSettings()); setEditOpen(true); }
        catch (err) { toast.showApiError(err, 'Failed to load the KPI settings'); }
    };

    const saveSettings = async () => {
        if (!settings) return;
        const { defaults, ...body } = settings;
        try { await FpaService.saveKpiSettings(body); toast.showSuccess('KPI settings saved'); setEditOpen(false); await load(); }
        catch (err) { toast.showApiError(err, 'Could not save the settings'); }
    };

    return (
        <Box data-help-page="accounting.kpi.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>KPI Pack</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <TextField data-help="accounting.kpi.asOf" label="As of" type="date" size="small" value={asOf} onChange={e => setAsOf(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                <Box sx={{ flex: '1 1 200px', maxWidth: 260 }}>
                    <SearchableSelect data-help="accounting.kpi.segment" options={segments} value={segmentId} onChange={setSegmentId} getOptionLabel={s => `${s.code} ${s.name}`} label="Segment" size="small" />
                </Box>
                <Button data-help="accounting.kpi.apply" variant="contained" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Apply'}</Button>
                {canManage && <Button data-help="accounting.kpi.settings.open" variant="outlined" onClick={openSettings}>Settings</Button>}
            </Box>
            {result && (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>As of {result.asOf}; flow ratios use the {result.windowDays} days from {result.windowFrom}. Hover a tile to see how it is computed.</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {result.kpis.map(k => (
                            <Tooltip key={k.key} arrow title={<Box><div>{k.howComputed}</div>{k.inputs.map(i => <div key={i}>{i}</div>)}</Box>}>
                                <Card variant="outlined" sx={{ flex: '1 1 200px', maxWidth: 260 }} data-testid={`kpi-${k.key}`}>
                                    <CardContent>
                                        <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                                        <Typography variant="h5">{formatKpi(k.value, k.unit)}</Typography>
                                        {k.value === null && <Typography variant="caption" color="text.secondary">Not computable (zero denominator)</Typography>}
                                    </CardContent>
                                </Card>
                            </Tooltip>
                        ))}
                    </Box>
                </>
            )}

            <Dialog data-help-page="accounting.kpi.settings.page" open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>KPI settings</DialogTitle>
                <DialogContent>
                    {settings && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                            <Alert severity="info" sx={{ flex: '1 1 100%' }}>Account-code prefixes, comma separated. Defaults follow the standard chart.</Alert>
                            <TextField data-help="accounting.kpi.settings.field.thresholdPct" label="Variance threshold %" type="number" size="small" value={settings.varianceThresholdPct} sx={{ flex: '1 1 160px', maxWidth: 240 }}
                                onChange={e => setSettings({ ...settings, varianceThresholdPct: Number(e.target.value) })} />
                            <TextField data-help="accounting.kpi.settings.field.minAmount" label="Variance min amount" type="number" size="small" value={settings.varianceMinAmount} sx={{ flex: '1 1 160px', maxWidth: 240 }}
                                onChange={e => setSettings({ ...settings, varianceMinAmount: Number(e.target.value) })} />
                            <TextField data-help="accounting.kpi.settings.field.window" label="Window (months)" type="number" size="small" value={settings.windowMonths} sx={{ flex: '1 1 160px', maxWidth: 240 }}
                                onChange={e => setSettings({ ...settings, windowMonths: Number(e.target.value) })} />
                            {PREFIX_FIELDS.map(([k, label]) => (
                                <TextField data-help="accounting.kpi.settings.field.prefix" key={k} label={label} size="small" value={(settings[k] as string) ?? ''} sx={{ flex: '1 1 220px', maxWidth: 300 }}
                                    onChange={e => setSettings({ ...settings, [k]: e.target.value })} />
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions><Button data-help="accounting.kpi.settings.cancel" onClick={() => setEditOpen(false)}>Cancel</Button><Button data-help="accounting.kpi.settings.save" variant="contained" onClick={saveSettings}>Save</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default KpiPack;
